import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FleetKpi, getFleetKpi } from "../../api";
import { Icon, type IconKey } from "../../icons";

interface KpiRow {
  icon: IconKey;
  label: string;
  value: string | number;
  tone: "indigo" | "rose" | "emerald" | "amber" | "violet";
  hint?: string;
}

function pct(part: number, total: number): string {
  if (!total) return "—";
  return `${Math.round((part / total) * 100)}%`;
}

export function ReportsPage() {
  const { t, i18n } = useTranslation(["dashboard", "common"]);
  const [kpi, setKpi] = useState<FleetKpi | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFleetKpi()
      .then(setKpi)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="muted">{t("common:actions.loading")}</p>;
  if (error) return <p className="error">{error}</p>;
  if (!kpi) return <p className="muted">{t("reports.noData")}</p>;

  const rows: KpiRow[] = [
    { icon: "truck", label: t("reports.kpi.vehiclesTotal"), value: kpi.vehicles_total, tone: "indigo" },
    { icon: "blocks", label: t("reports.kpi.fleetsTotal"), value: kpi.fleets_total, tone: "indigo" },
    {
      icon: "alert",
      label: t("reports.kpi.alertsOpen"),
      value: kpi.alerts_open,
      tone: kpi.alerts_open > 0 ? "rose" : "emerald",
      hint: kpi.alerts_critical_open > 0 ? t("reports.hint.critical", { count: kpi.alerts_critical_open }) : t("reports.hint.noCritical"),
    },
    {
      icon: "bolt",
      label: t("reports.kpi.todayEvents"),
      value: kpi.alerts_today,
      tone: "violet",
      hint: t("reports.hint.last24h"),
    },
    {
      icon: "fuel",
      label: t("reports.kpi.fuelAnomalies"),
      value: kpi.fuel_anomalies_open,
      tone: kpi.fuel_anomalies_open > 0 ? "amber" : "emerald",
    },
    {
      icon: "bell",
      label: t("reports.kpi.notifications"),
      value: `${kpi.notifications_sent_24h}/${kpi.notifications_24h}`,
      tone: kpi.notifications_failed_24h > 0 ? "amber" : "emerald",
      hint:
        kpi.notifications_failed_24h > 0
          ? t("reports.hint.failed", { count: kpi.notifications_failed_24h })
          : t("reports.hint.successRate", { rate: pct(kpi.notifications_sent_24h, kpi.notifications_24h) }),
    },
  ];

  return (
    <div className="page">
      <div className="page-head-row">
        <div>
          <h2 style={{ marginTop: 0 }}>
            <Icon name="chart" /> {t("reports.title")}
          </h2>
          <p className="muted">
            {t("reports.intro", { date: new Date(kpi.generated_at).toLocaleString(i18n.resolvedLanguage) })}
          </p>
        </div>
      </div>

      {kpi.degraded.length > 0 && (
        <p className="warn-banner">
          <Icon name="alert" /> {t("reports.degraded", { services: kpi.degraded.join(", ") })}
        </p>
      )}

      <div className="kpi-grid">
        {rows.map((r) => (
          <div key={r.label} className={`kpi-card tone-${r.tone}`}>
            <div className="kpi-icon">
              <Icon name={r.icon} size={22} />
            </div>
            <div className="kpi-body">
              <div className="kpi-label">{r.label}</div>
              <div className="kpi-value">{r.value}</div>
              {r.hint && <div className="kpi-sub">{r.hint}</div>}
            </div>
          </div>
        ))}
      </div>

      <h3 style={{ marginTop: "1.5rem" }}>{t("reports.severityDist")}</h3>
      <DistributionTable data={kpi.alerts_by_severity} emptyLabel={t("reports.noAlerts")} />

      <h3 style={{ marginTop: "1.5rem" }}>{t("reports.statusDist")}</h3>
      <DistributionTable data={kpi.alerts_by_status} emptyLabel={t("reports.noAlerts")} />

      <p className="muted small" style={{ marginTop: "1.5rem" }}>
        {t("reports.exportNote")}
      </p>
    </div>
  );
}

function DistributionTable({
  data,
  emptyLabel,
}: {
  data: Record<string, number>;
  emptyLabel: string;
}) {
  const { t } = useTranslation("dashboard");
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((acc, [, v]) => acc + v, 0);
  if (entries.length === 0 || total === 0) {
    return <p className="muted small">{emptyLabel}</p>;
  }
  return (
    <table>
      <thead>
        <tr>
          <th>{t("reports.distTh.label")}</th>
          <th>{t("reports.distTh.count")}</th>
          <th>{t("reports.distTh.share")}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(([k, v]) => (
          <tr key={k}>
            <td>{k}</td>
            <td>{v}</td>
            <td>{Math.round((v / total) * 100)}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
